import LanguageTag from './LanguageTag';

let _locale = 'en-US';
let _languageTag = new LanguageTag(_locale);

/**
 * Helper for getting year and month from date string
 *
 * @param {String} dateString   Date string in yyyy-mm-dd format
 * @return {Object} {month, year}
 */
function getMonthYear(dateString) {
    if (dateString) {
        const dateElems = dateString.split('-');
        return {month: +dateElems[1] - 1, year: +dateElems[0]};
    }

    const today = new Date();
    return {month: today.getMonth(), year: today.getFullYear()};
}

/**
 * Helper for getting string with a language sensitive representation of the date portion of this date.
 *
 * @param {String} dateString   Date string in yyyy-mm-dd format
 * @param {String} locale   Moniker or BCP-47 language tag (fr_FR_HA|fr-FR)
 * @param {Object} options  Date.prototype.toLocaleDateString() options ( https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString)
 * @return {String} Language sensitive representation of the date
 */
function getLocaleDateString(dateString, locale, options) {
    locale = locale || _locale;
    if (!dateString) {
        return '';
    }

    if (locale !== _locale) {
        _locale = locale;
        _languageTag = new LanguageTag(_locale);
    }
    const parts = dateString.split('-');
    const dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
    return dateObj.toLocaleDateString(_languageTag.code, options);
}

/**
 * Helper for getting language sensitive representation of the date range.
 *
 * @param {String} startDateString   Date string in yyyy-mm-dd format
 * @param {String} endDateString   Date string in yyyy-mm-dd format
 * @param {String} locale   Moniker or BCP-47 language tag (fr_FR_HA|fr-FR)
 * @param {Object} options  Date.prototype.toLocaleDateString() options ( https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString)
 * @return {String} Language sensitive representation of the dates
 */
function getLocaleConsolidatedDateString(
    startDateString,
    endDateString,
    locale,
    options = {}
) {
    // eslint-disable-line complexity
    if (!startDateString || !endDateString) {
        return getLocaleDateString(
            startDateString || endDateString,
            locale,
            options
        );
    }

    const startDate = getLocaleDateString(startDateString, locale, options);
    const endDate = getLocaleDateString(endDateString, locale, options);
    const sameMonth =
        startDateString.split('-')[1] === endDateString.split('-')[1];
    const dedupeMonth =
        Object.keys(options).length === 2 && !!options.day && !!options.month;

    if (sameMonth && dedupeMonth) {
        const startDateDate = getLocaleDateString(startDateString, locale, {
            day: 'numeric',
        });
        const endDateDate = getLocaleDateString(endDateString, locale, {
            day: 'numeric',
        });

        // Use "Sep 5 - 30" or "05. - 30. Sep."
        const isDecimalDay = startDate.indexOf(`${startDateDate}.`) >= 0;
        const formattedDateRange = startDate.replace(
            startDateDate,
            `${startDateDate}${isDecimalDay ? '.' : ''} - ${endDateDate}`
        );
        return formattedDateRange;
    }

    // Use "mmm dd - mmm dd" or "mm/dd/yyyy - mm/dd/yyyy"
    return `${startDate} - ${endDate}`;
}

/**
 * Returns string representation of a date when given a date object.
 * The result is in the format "YYYY-MM-DD".
 *
 * @param {Date} date - date object
 * @return {string}  string representation of date.
 */
function getDateToString(date) {
    let month = date.getMonth() + 1;
    month = month < 10 ? `0${month}` : month;
    let day = date.getDate();
    day = day < 10 ? `0${day}` : day;
    return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Compares 2 date strings and returns an integer that indicates whether the
 * second date is earlier (-1), later (1), or equal to (0) the first.
 *
 * @param {string} startString - start date string in format, "YYYY-MM-DD"
 * @param {string} endString - end date string in format, "YYYY-MM-DD"
 * @return {int}  indication or comparison
 */
function compareDateStrings(startString, endString) {
    const difference = Date.parse(endString) - Date.parse(startString);

    if (difference > 0) {
        return 1;
    } else if (difference < 0) {
        return -1;
    }

    return 0;
}

/**
 * Returns either the overridden date text or the formatted version of the YYYY-MM-DD date string.
 * The override is only used if the dateString is defined.
 *
 * @param {String} dateString    Date string in yyyy-mm-dd format
 * @param {String} overrideText  Any string which overrides the formatted date if present
 * @param {String} locale        Moniker or BCP-47 language tag (fr_FR_HA|fr-FR)
 * @param {Object} options       Date.prototype.toLocaleDateString() options (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString)
 * @returns {String}             Language sensitive representation of the date OR override
 */
function getEffectiveDateText(dateString, overrideText, locale, options) {
    if (dateString && overrideText) {
        return overrideText;
    }

    return getLocaleDateString(dateString, locale, options);
}

/**
 * Helper for getting language sensitive representation of the date range allowing overriding start and/or end date.
 *
 * @param startDate         Start date string in yyyy-mm-dd format
 * @param startDateText     Any string which overrides the formatted start date if present
 * @param endDate           End date string in yyyy-mm-dd format
 * @param endDateText       Any string which overrides the formatted end date if present
 * @param locale            Moniker or BCP-47 language tag (fr_FR_HA|fr-FR)
 * @param options           Date.prototype.toLocaleDateString() options (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString)
 * @returns {String|string}  Language sensitive representation of the dates with overrides if needed
 */
function getEffectiveConsolidatedDateString(
    startDate,
    startDateText,
    endDate,
    endDateText,
    locale,
    options
) {
    if ((startDate && startDateText) || (endDate && endDateText)) {
        const effectiveStartDate = getEffectiveDateText(
            startDate,
            startDateText,
            locale,
            options
        );
        const effectiveEndDate = getEffectiveDateText(
            endDate,
            endDateText,
            locale,
            options
        );

        return `${effectiveStartDate} - ${effectiveEndDate}`;
    }

    return getLocaleConsolidatedDateString(startDate, endDate, locale, options);
}

export {
    getMonthYear,
    getLocaleDateString,
    getLocaleConsolidatedDateString,
    getDateToString,
    compareDateStrings,
    getEffectiveDateText,
    getEffectiveConsolidatedDateString,
};
